// A franchisor's Brand HQ view of its own subtree (ADR-0019 Phase 2, F-ORG-17; spec/07 §6.1, §7).
//
//   GET /v1/organizations/{o}/franchisees                     the org's direct franchisees   franchise:read
//   GET /v1/organizations/{o}/franchisees/{f}/venues          one franchisee's venues        franchise:read
//
// A **franchisor** (`organization.kind = franchisor`) owns its own venues directly and is the
// `franchisorOrganizationId` of its franchisees (ADR-0019). A Brand HQ lets an authorised member of the
// franchisor read across its **own subtree only** — the organisations whose franchisor is itself, and
// those franchisees' venues — never a sibling brand's franchisees, never an unrelated organisation.
// This is a **bounded** access rule, not the Provider's platform-wide bypass (spec/07 §7): the read is
// narrowed in the database to exactly the franchisor's direct franchisees, and every cross-organisation
// read is audited in the franchisee's own log, exactly as the Provider's cross-org read is (§11.2).
//
// The boundary is the franchise link, checked server-side against `auth.organization`: a `{franchiseeId}`
// whose `franchisorOrganizationId` is not this organisation is `404`, indistinguishable from an id that
// was never a franchisee of anyone — a franchisee of another brand, or a top-level organisation, answers
// the same as a stranger (spec/08 §2.6). This phase is **read-first**: there is no cross-organisation
// write here (`franchise:manage` and its endpoints are a later phase, ADR-0019 Phase 5).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema } from "../ids/schema.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { FRANCHISE_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { OrganizationKindSchema, OrganizationStatusSchema } from "./hq-organizations.js";
import { VenueSchema } from "./venues.js";
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/**
 * One of a franchisor's direct franchisees, as its Brand HQ lists it: the franchisee organisation's
 * own record, not a reach into its data. `franchisorOrganizationId` is the franchisor this list belongs
 * to, echoed so a client never has to infer it.
 */
export const FranchiseeSchema = z
    .object({
    id: OrganizationIdSchema,
    name: z.string(),
    slug: z.string(),
    kind: OrganizationKindSchema,
    status: OrganizationStatusSchema,
    /** The franchisor this franchisee belongs to — the organisation in the path (ADR-0019). */
    franchisorOrganizationId: OrganizationIdSchema,
    createdAt: IsoDateTimeSchema,
})
    .openapi('Franchisee', {
    description: "A franchisor's direct franchisee, as its Brand HQ view lists it (ADR-0019 Phase 2).",
});
export const FranchiseeListSchema = paginated(FranchiseeSchema, 'FranchiseeList');
/** A page of a franchisee's venues, as its franchisor's Brand HQ reads them. The venue is the ordinary shape. */
export const FranchiseeVenueListSchema = paginated(VenueSchema, 'FranchiseeVenueList');
/** `{organizationId}` the franchisor, `{franchiseeId}` one of its direct franchisees. Both organisation ids. */
export const FranchiseeScopeParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, franchiseeId: OrganizationIdSchema })
    .openapi('FranchiseeScopeParams');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
export const listFranchiseesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/franchisees',
    operationId: 'listFranchisees',
    tags: ['franchise'],
    summary: "List an organisation's direct franchisees",
    description: "The organisations whose franchisor is this one (its direct franchisees), oldest first — a franchisor's " +
        'Brand HQ view of its own subtree (ADR-0019 Phase 2). An organisation with no franchisees answers an empty ' +
        "page. Each franchisee's record read is audited in that franchisee's own log. Requires `franchise:read`.",
    [PERMISSION_EXTENSION]: FRANCHISE_READ,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: { description: 'A page of franchisees.', content: { [JSON_CONTENT_TYPE]: { schema: FranchiseeListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listFranchiseeVenuesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/franchisees/{franchiseeId}/venues',
    operationId: 'listFranchiseeVenues',
    tags: ['franchise'],
    summary: "Read one of an organisation's franchisees' venues",
    description: "The venues of one direct franchisee, oldest first, archived ones left out — a franchisor's Brand HQ view " +
        "(ADR-0019 Phase 2). `{franchiseeId}` must be a direct franchisee of `{organizationId}`; any other " +
        'organisation — a sibling brand’s franchisee, a top-level organisation, one that does not exist — is `404`, ' +
        "the same as a stranger. The read is audited in the franchisee's own log. Requires `franchise:read`.",
    [PERMISSION_EXTENSION]: FRANCHISE_READ,
    request: { params: FranchiseeScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: { description: "A page of the franchisee's venues.", content: { [JSON_CONTENT_TYPE]: { schema: FranchiseeVenueListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such franchisee of this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** Both Brand HQ franchisee reads (ADR-0019 Phase 2), for `V1_ROUTES`. */
export const FRANCHISE_ROUTES = [listFranchiseesRoute, listFranchiseeVenuesRoute];
//# sourceMappingURL=franchisees.js.map